import { DatePipe, DecimalPipe } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { CustomerSelectorComponent } from '../../shared/design-system/customer-selector.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import {
  BrokerageAccount,
  CatalogService,
  ComplianceScreenResult,
  PreciousMetalOrder,
  RegulatedFinanceDashboard
} from './catalog.service';

@Component({
  standalone: true,
  selector: 'ain-regulated-finance-cockpit',
  imports: [
    AinPageComponent, AinStateComponent, TableModule, ButtonModule, DatePipe, DecimalPipe,
    DialogModule, FormsModule, InputTextModule, CheckboxModule, CustomerSelectorComponent, SelectModule
  ],
  template: `
    <ain-page [title]="t('catalog.regFinance.cockpit.title')" [description]="t('catalog.regFinance.cockpit.description')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else {
        <section class="panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('catalog.regFinance.cockpit.createAccount')" severity="secondary" (click)="openAccountDialog()"></button>
            <button pButton type="button" [label]="t('catalog.regFinance.cockpit.pmOrder')" severity="secondary" (click)="openPmDialog()"></button>
            <button pButton type="button" [label]="t('catalog.regFinance.cockpit.brokerageOrder')" severity="secondary" (click)="openBrokerageDialog()"></button>
            <button pButton type="button" [label]="t('catalog.regFinance.cockpit.insurancePolicy')" severity="secondary" (click)="openInsuranceDialog()"></button>
            <button pButton type="button" [label]="t('catalog.regFinance.cockpit.fxTrade')" severity="secondary" (click)="openFxDialog()"></button>
            <button pButton type="button" [label]="t('catalog.regFinance.cockpit.runScreen')" severity="secondary" (click)="openScreenDialog()"></button>
            <button pButton type="button" [label]="t('catalog.action.refresh')" severity="secondary" (click)="load()"></button>
          </div>
          @if (message()) { <p class="message">{{ message() }}</p> }

          <div class="stats">
            <div class="stat"><span class="label">{{ t('catalog.regFinance.cockpit.pmOrders') }}</span><span class="value">{{ dashboard()?.preciousMetalOrderCount ?? 0 }}</span></div>
            <div class="stat"><span class="label">{{ t('catalog.regFinance.cockpit.brokerageOrders') }}</span><span class="value">{{ dashboard()?.brokerageOrderCount ?? 0 }}</span></div>
            <div class="stat"><span class="label">{{ t('catalog.regFinance.cockpit.policies') }}</span><span class="value">{{ dashboard()?.insurancePolicyCount ?? 0 }}</span></div>
            <div class="stat"><span class="label">{{ t('catalog.regFinance.cockpit.openClaims') }}</span><span class="value">{{ dashboard()?.openInsuranceClaimCount ?? 0 }}</span></div>
            <div class="stat"><span class="label">{{ t('catalog.regFinance.cockpit.fxTrades') }}</span><span class="value">{{ dashboard()?.fxTradeCount ?? 0 }}</span></div>
            <div class="stat"><span class="label">{{ t('catalog.regFinance.cockpit.hardStops') }}</span><span class="value">{{ dashboard()?.hardStopCount ?? 0 }}</span></div>
          </div>

          <h3>{{ t('catalog.regFinance.cockpit.accounts') }}</h3>
          <p-table [value]="accounts()" [paginator]="true" [rows]="5">
            <ng-template pTemplate="header">
              <tr><th>{{ t('catalog.regFinance.cockpit.accountNumber') }}</th><th>{{ t('catalog.regFinance.cockpit.suitabilityApproved') }}</th></tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.accountNumber }}</td>
                <td>{{ row.suitabilityApproved ? '✓' : '—' }}</td>
              </tr>
            </ng-template>
          </p-table>

          <h3>{{ t('catalog.regFinance.cockpit.recentPm') }}</h3>
          <p-table [value]="dashboard()?.recentPreciousMetalOrders ?? []" [paginator]="true" [rows]="5">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('catalog.regFinance.cockpit.side') }}</th>
                <th>{{ t('catalog.tracking.status') }}</th>
                <th>{{ t('catalog.regFinance.cockpit.quantity') }}</th>
                <th></th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.side }}</td>
                <td>{{ row.status }}</td>
                <td>{{ row.quantityGrams | number:'1.2-2' }}</td>
                <td>
                  @if (row.status === 'Submitted') {
                    <button pButton type="button" size="small" [label]="t('catalog.regFinance.cockpit.settle')" [disabled]="acting()" (click)="openSettlePm(row)"></button>
                  }
                </td>
              </tr>
            </ng-template>
          </p-table>

          <h3>{{ t('catalog.regFinance.cockpit.recentBrokerage') }}</h3>
          <p-table [value]="dashboard()?.recentBrokerageOrders ?? []" [paginator]="true" [rows]="5">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('catalog.regFinance.cockpit.symbol') }}</th>
                <th>{{ t('catalog.tracking.status') }}</th>
                <th></th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.symbol }}</td>
                <td>{{ row.status }}</td>
                <td>
                  @if (row.status === 'Submitted') {
                    <button pButton type="button" size="small" [label]="t('catalog.regFinance.cockpit.execute')" [disabled]="acting()" (click)="executeBrokerage(row.id)"></button>
                  }
                </td>
              </tr>
            </ng-template>
          </p-table>

          <h3>{{ t('catalog.regFinance.cockpit.recentPolicies') }}</h3>
          <p-table [value]="dashboard()?.recentInsurancePolicies ?? []" [paginator]="true" [rows]="5">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('catalog.regFinance.cockpit.policyNumber') }}</th>
                <th>{{ t('catalog.regFinance.cockpit.premium') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.policyNumber }}</td>
                <td>{{ row.premiumAmount | number:'1.2-2' }} {{ row.currencyCode }}</td>
              </tr>
            </ng-template>
          </p-table>

          <h3>{{ t('catalog.regFinance.cockpit.recentFx') }}</h3>
          <p-table [value]="dashboard()?.recentFxTrades ?? []" [paginator]="true" [rows]="5">
            <ng-template pTemplate="header">
              <tr><th>{{ t('catalog.tracking.status') }}</th><th>{{ t('catalog.regFinance.cockpit.baseAmount') }}</th><th>{{ t('catalog.regFinance.cockpit.rate') }}</th></tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.status }}</td>
                <td>{{ row.baseAmount | number:'1.2-2' }}</td>
                <td>{{ row.rate | number:'1.4-4' }}</td>
              </tr>
            </ng-template>
          </p-table>

          <h3>{{ t('catalog.regFinance.cockpit.recentCompliance') }}</h3>
          <p-table [value]="dashboard()?.recentComplianceScreens ?? []" [paginator]="true" [rows]="5">
            <ng-template pTemplate="header">
              <tr><th>{{ t('catalog.tracking.status') }}</th><th>{{ t('catalog.regFinance.cockpit.hardStop') }}</th><th>{{ t('catalog.regFinance.cockpit.notes') }}</th></tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr><td>{{ row.result }}</td><td>{{ row.isHardStop ? '!' : '—' }}</td><td>{{ row.decisionNotes }}</td></tr>
            </ng-template>
          </p-table>
        </section>
      }

      <p-dialog [header]="t('catalog.regFinance.cockpit.createAccount')" [(visible)]="accountDialogOpen" [modal]="true" [style]="{ width: '420px' }">
        <div class="form">
          <label>{{ t('catalog.regFinance.cockpit.customer') }}</label>
          <ain-customer-selector [(ngModel)]="accountCustomerId" />
          <label>{{ t('catalog.regFinance.cockpit.accountNumber') }}</label>
          <input pInputText [(ngModel)]="accountNumber" class="full" />
          <div class="check-row">
            <p-checkbox [(ngModel)]="suitabilityApproved" [binary]="true" inputId="suit" />
            <label for="suit">{{ t('catalog.regFinance.cockpit.suitabilityApproved') }}</label>
          </div>
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="accountDialogOpen = false"></button>
          <button pButton type="button" [label]="t('catalog.action.create')" [disabled]="acting()" (click)="createAccount()"></button>
        </ng-template>
      </p-dialog>

      <p-dialog [header]="t('catalog.regFinance.cockpit.pmOrder')" [(visible)]="pmDialogOpen" [modal]="true" [style]="{ width: '420px' }">
        <div class="form">
          <label>{{ t('catalog.regFinance.cockpit.side') }}</label>
          <p-select [options]="sideOptions" optionLabel="label" optionValue="value" [(ngModel)]="pmSide" [style]="{ width: '100%' }" />
          <label>{{ t('catalog.regFinance.cockpit.quantity') }}</label>
          <input pInputText type="number" [(ngModel)]="pmQuantity" class="full" />
          <label>{{ t('catalog.regFinance.cockpit.limitPrice') }}</label>
          <input pInputText type="number" [(ngModel)]="pmLimitPrice" class="full" />
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="pmDialogOpen = false"></button>
          <button pButton type="button" [label]="t('catalog.action.create')" [disabled]="acting()" (click)="submitPmOrder()"></button>
        </ng-template>
      </p-dialog>

      <p-dialog [header]="t('catalog.regFinance.cockpit.settle')" [(visible)]="settleDialogOpen" [modal]="true" [style]="{ width: '400px' }">
        <div class="form">
          <label>{{ t('catalog.regFinance.cockpit.settleAmount') }}</label>
          <input pInputText type="number" [(ngModel)]="settleAmount" class="full" />
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="settleDialogOpen = false"></button>
          <button pButton type="button" [label]="t('catalog.regFinance.cockpit.settle')" [disabled]="acting()" (click)="confirmSettlePm()"></button>
        </ng-template>
      </p-dialog>

      <p-dialog [header]="t('catalog.regFinance.cockpit.brokerageOrder')" [(visible)]="brokerageDialogOpen" [modal]="true" [style]="{ width: '440px' }">
        <div class="form">
          <label>{{ t('catalog.regFinance.cockpit.account') }}</label>
          <p-select [options]="accountOptions()" optionLabel="label" optionValue="value" [(ngModel)]="brokerageAccountId" [style]="{ width: '100%' }" />
          <label>{{ t('catalog.regFinance.cockpit.symbol') }}</label>
          <input pInputText [(ngModel)]="brokerageSymbol" class="full" />
          <label>{{ t('catalog.regFinance.cockpit.side') }}</label>
          <p-select [options]="sideOptions" optionLabel="label" optionValue="value" [(ngModel)]="brokerageSide" [style]="{ width: '100%' }" />
          <label>{{ t('catalog.regFinance.cockpit.shares') }}</label>
          <input pInputText type="number" [(ngModel)]="brokerageQuantity" class="full" />
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="brokerageDialogOpen = false"></button>
          <button pButton type="button" [label]="t('catalog.action.create')" [disabled]="acting()" (click)="submitBrokerageOrder()"></button>
        </ng-template>
      </p-dialog>

      <p-dialog [header]="t('catalog.regFinance.cockpit.insurancePolicy')" [(visible)]="insuranceDialogOpen" [modal]="true" [style]="{ width: '440px' }">
        <div class="form">
          <label>{{ t('catalog.regFinance.cockpit.customer') }}</label>
          <ain-customer-selector [(ngModel)]="insuranceCustomerId" />
          <label>{{ t('catalog.regFinance.cockpit.policyNumber') }}</label>
          <input pInputText [(ngModel)]="policyNumber" class="full" />
          <label>{{ t('catalog.regFinance.cockpit.premium') }}</label>
          <input pInputText type="number" [(ngModel)]="policyPremium" class="full" />
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="insuranceDialogOpen = false"></button>
          <button pButton type="button" [label]="t('catalog.action.create')" [disabled]="acting()" (click)="submitInsurancePolicy()"></button>
        </ng-template>
      </p-dialog>

      <p-dialog [header]="t('catalog.regFinance.cockpit.fxTrade')" [(visible)]="fxDialogOpen" [modal]="true" [style]="{ width: '420px' }">
        <div class="form">
          <label>{{ t('catalog.regFinance.cockpit.baseCurrency') }}</label>
          <input pInputText [(ngModel)]="fxBaseCurrency" class="full" />
          <label>{{ t('catalog.regFinance.cockpit.quoteCurrency') }}</label>
          <input pInputText [(ngModel)]="fxQuoteCurrency" class="full" />
          <label>{{ t('catalog.regFinance.cockpit.baseAmount') }}</label>
          <input pInputText type="number" [(ngModel)]="fxBaseAmount" class="full" />
          <label>{{ t('catalog.regFinance.cockpit.rate') }}</label>
          <input pInputText type="number" [(ngModel)]="fxRate" class="full" />
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="fxDialogOpen = false"></button>
          <button pButton type="button" [label]="t('catalog.action.create')" [disabled]="acting()" (click)="submitFxTrade()"></button>
        </ng-template>
      </p-dialog>

      <p-dialog [header]="t('catalog.regFinance.cockpit.runScreen')" [(visible)]="screenDialogOpen" [modal]="true" [style]="{ width: '420px' }">
        <div class="form">
          <label>{{ t('catalog.regFinance.cockpit.customer') }}</label>
          <ain-customer-selector [(ngModel)]="screenCustomerId" />
          <label>{{ t('catalog.regFinance.cockpit.screeningType') }}</label>
          <input pInputText [(ngModel)]="screeningType" class="full" />
          <div class="check-row">
            <p-checkbox [(ngModel)]="simulateHardStop" [binary]="true" inputId="hard" />
            <label for="hard">{{ t('catalog.regFinance.cockpit.simulateHardStop') }}</label>
          </div>
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="screenDialogOpen = false"></button>
          <button pButton type="button" [label]="t('catalog.regFinance.cockpit.runScreen')" [disabled]="acting()" (click)="runScreen()"></button>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .panel { display: grid; gap: 16px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; }
    .stats { display: grid; grid-template-columns: repeat(auto-fill, minmax(120px, 1fr)); gap: 12px; }
    .stat { border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px; display: grid; gap: 4px; }
    .label { font-size: 0.8rem; color: #6b7280; }
    .value { font-size: 1.2rem; font-weight: 700; }
    .message { margin: 0; color: #166534; font-weight: 600; }
    h3 { margin: 0; font-size: 1rem; }
    .form { display: grid; gap: 8px; }
    .full { width: 100%; }
    .check-row { display: flex; align-items: center; gap: 8px; }
  `]
})
export class RegulatedFinanceCockpitComponent implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly acting = signal(false);
  readonly dashboard = signal<RegulatedFinanceDashboard | null>(null);
  readonly message = signal('');
  readonly accounts = signal<BrokerageAccount[]>([]);

  readonly sideOptions = [
    { label: 'Buy', value: 'Buy' },
    { label: 'Sell', value: 'Sell' }
  ];

  accountDialogOpen = false;
  pmDialogOpen = false;
  settleDialogOpen = false;
  brokerageDialogOpen = false;
  insuranceDialogOpen = false;
  fxDialogOpen = false;
  screenDialogOpen = false;

  accountNumber = '';
  accountCustomerId: string | null = null;
  suitabilityApproved = true;

  pmSide = 'Buy';
  pmQuantity = 10;
  pmLimitPrice = 2000;

  settlePmId = '';
  settleAmount = 0;

  brokerageAccountId = '';
  brokerageSymbol = 'AAPL';
  brokerageSide = 'Buy';
  brokerageQuantity = 10;

  insuranceCustomerId: string | null = null;
  policyNumber = '';
  policyPremium = 500;

  fxBaseCurrency = 'USD';
  fxQuoteCurrency = 'EUR';
  fxBaseAmount = 1000;
  fxRate = 0.92;

  screenCustomerId: string | null = null;
  screeningType = 'Sanctions';
  simulateHardStop = false;

  ngOnInit(): void { this.load(); }

  accountOptions(): { label: string; value: string }[] {
    return this.accounts()
      .filter(a => a.suitabilityApproved)
      .map(a => ({ label: a.accountNumber, value: a.id }));
  }

  load(): void {
    this.loading.set(true);
    this.service.getRegulatedFinanceDashboard().subscribe({
      next: d => { this.dashboard.set(d); this.loading.set(false); },
      error: () => { this.dashboard.set(null); this.loading.set(false); }
    });
    this.service.listBrokerageAccounts().subscribe({ next: a => this.accounts.set(a) });
  }

  openAccountDialog(): void {
    this.accountCustomerId = null;
    this.accountNumber = `BRK-${Date.now().toString().slice(-6)}`;
    this.suitabilityApproved = true;
    this.accountDialogOpen = true;
  }

  createAccount(): void {
    if (!this.accountNumber.trim() || !this.accountCustomerId) {
      this.message.set(this.t('catalog.customer.required'));
      return;
    }
    this.acting.set(true);
    this.service.createBrokerageAccount({
      accountNumber: this.accountNumber.trim(),
      customerId: this.accountCustomerId,
      suitabilityApproved: this.suitabilityApproved
    }).subscribe({
      next: () => {
        this.accountDialogOpen = false;
        this.message.set(this.t('catalog.regFinance.cockpit.accountCreated'));
        this.load();
        this.acting.set(false);
      },
      error: () => this.acting.set(false)
    });
  }

  openPmDialog(): void {
    this.pmSide = 'Buy';
    this.pmQuantity = 10;
    this.pmLimitPrice = 2000;
    this.pmDialogOpen = true;
  }

  submitPmOrder(): void {
    this.acting.set(true);
    this.service.submitPreciousMetalOrder({
      side: this.pmSide,
      quantityGrams: this.pmQuantity,
      limitPrice: this.pmLimitPrice
    }).subscribe({
      next: () => {
        this.pmDialogOpen = false;
        this.message.set(this.t('catalog.regFinance.cockpit.pmSubmitted'));
        this.load();
        this.acting.set(false);
      },
      error: () => this.acting.set(false)
    });
  }

  openSettlePm(row: PreciousMetalOrder): void {
    this.settlePmId = row.id;
    this.settleAmount = Math.round(row.quantityGrams * 2000 * 100) / 100;
    this.settleDialogOpen = true;
  }

  confirmSettlePm(): void {
    if (!this.settlePmId || this.settleAmount <= 0) return;
    this.acting.set(true);
    this.service.settlePreciousMetalOrder(this.settlePmId, this.settleAmount, 'USD').subscribe({
      next: () => {
        this.settleDialogOpen = false;
        this.message.set(this.t('catalog.regFinance.cockpit.pmSettled'));
        this.load();
        this.acting.set(false);
      },
      error: () => this.acting.set(false)
    });
  }

  openBrokerageDialog(): void {
    const approved = this.accounts().filter(a => a.suitabilityApproved);
    this.brokerageAccountId = approved[0]?.id ?? '';
    this.brokerageSymbol = 'AAPL';
    this.brokerageSide = 'Buy';
    this.brokerageQuantity = 10;
    this.brokerageDialogOpen = true;
  }

  submitBrokerageOrder(): void {
    if (!this.brokerageAccountId) {
      this.message.set(this.t('catalog.regFinance.cockpit.createAccount'));
      return;
    }
    this.acting.set(true);
    this.service.submitBrokerageOrder({
      accountId: this.brokerageAccountId,
      symbol: this.brokerageSymbol.trim(),
      side: this.brokerageSide,
      quantity: this.brokerageQuantity
    }).subscribe({
      next: () => {
        this.brokerageDialogOpen = false;
        this.message.set(this.t('catalog.regFinance.cockpit.brokerageSubmitted'));
        this.load();
        this.acting.set(false);
      },
      error: () => this.acting.set(false)
    });
  }

  executeBrokerage(orderId: string): void {
    this.acting.set(true);
    this.service.executeBrokerageOrder(orderId).subscribe({
      next: () => {
        this.message.set(this.t('catalog.regFinance.cockpit.brokerageExecuted'));
        this.load();
        this.acting.set(false);
      },
      error: () => this.acting.set(false)
    });
  }

  openInsuranceDialog(): void {
    this.insuranceCustomerId = null;
    this.policyNumber = `POL-${Date.now().toString().slice(-6)}`;
    this.policyPremium = 500;
    this.insuranceDialogOpen = true;
  }

  submitInsurancePolicy(): void {
    if (!this.policyNumber.trim() || !this.insuranceCustomerId) {
      this.message.set(this.t('catalog.customer.required'));
      return;
    }
    const from = new Date();
    const to = new Date(from);
    to.setFullYear(to.getFullYear() + 1);
    this.acting.set(true);
    this.service.issueInsurancePolicy({
      policyNumber: this.policyNumber.trim(),
      customerId: this.insuranceCustomerId,
      premiumAmount: this.policyPremium,
      currencyCode: 'USD',
      effectiveFromUtc: from.toISOString(),
      effectiveToUtc: to.toISOString()
    }).subscribe({
      next: () => {
        this.insuranceDialogOpen = false;
        this.message.set(this.t('catalog.regFinance.cockpit.policyIssued'));
        this.load();
        this.acting.set(false);
      },
      error: () => this.acting.set(false)
    });
  }

  openFxDialog(): void {
    this.fxBaseCurrency = 'USD';
    this.fxQuoteCurrency = 'EUR';
    this.fxBaseAmount = 1000;
    this.fxRate = 0.92;
    this.fxDialogOpen = true;
  }

  submitFxTrade(): void {
    this.acting.set(true);
    this.service.executeFxTrade({
      baseCurrency: this.fxBaseCurrency.trim(),
      quoteCurrency: this.fxQuoteCurrency.trim(),
      baseAmount: this.fxBaseAmount,
      rate: this.fxRate
    }).subscribe({
      next: () => {
        this.fxDialogOpen = false;
        this.message.set(this.t('catalog.regFinance.cockpit.fxExecuted'));
        this.load();
        this.acting.set(false);
      },
      error: () => this.acting.set(false)
    });
  }

  openScreenDialog(): void {
    this.screenCustomerId = null;
    this.screeningType = 'Sanctions';
    this.simulateHardStop = false;
    this.screenDialogOpen = true;
  }

  runScreen(): void {
    if (!this.screenCustomerId) {
      this.message.set(this.t('catalog.customer.required'));
      return;
    }
    this.acting.set(true);
    this.service.runComplianceScreen({
      subjectType: 'Customer',
      subjectId: this.screenCustomerId,
      screeningType: this.screeningType.trim(),
      simulateHardStop: this.simulateHardStop
    }).subscribe({
      next: (r: ComplianceScreenResult) => {
        this.screenDialogOpen = false;
        this.message.set(r.isHardStop
          ? this.t('catalog.regFinance.cockpit.screenHardStop')
          : this.t('catalog.regFinance.cockpit.screenPassed'));
        this.load();
        this.acting.set(false);
      },
      error: () => {
        this.message.set(this.t('catalog.regFinance.cockpit.screenHardStop'));
        this.screenDialogOpen = false;
        this.load();
        this.acting.set(false);
      }
    });
  }

  t(key: string): string { return this.localization.translate(key); }
}
